import type { ReactNode } from 'react';

import { useIntl } from '@skippay/core/i18n/intl';
import {
	type BackendError,
	type RichMessageValues,
	formatBackendError,
	formatGenericBackendError,
	normalizeBackendErrors,
} from '@skippay/core/utils/backendErrors';
import {
	extractError as extractErrorBase,
	getErrorMessages as getErrorMessagesBase,
	getValidationError as getValidationErrorBase,
	getValidationErrors as getValidationErrorsBase,
	toValidationErrors as toValidationErrorsBase,
} from '@skippay/core/utils/gqlUtils';

export function useBackendErrorMessages(): {
	extractError: typeof extractErrorBase;
	formatError: (error?: BackendError | null, richValues?: RichMessageValues) => string | ReactNode;
	formatGenericError: () => string;
	getErrorMessages: typeof getErrorMessagesBase;
	getValidationError: typeof getValidationErrorBase;
	getValidationErrors: typeof getValidationErrorsBase;
	normalizeBackendErrors: typeof normalizeBackendErrors;
	toValidationErrors: typeof toValidationErrorsBase;
} {
	const intl = useIntl();

	const formatError = (
		error?: BackendError | null,
		richValues?: RichMessageValues
	): string | ReactNode => formatBackendError(error, richValues, intl);
	const formatGenericError = () => formatGenericBackendError(intl);

	return {
		extractError: (originalError) => extractErrorBase(originalError, formatError),
		formatError,
		formatGenericError,
		getErrorMessages: (originalError) => getErrorMessagesBase(originalError, formatError),
		getValidationError: (apolloError) => getValidationErrorBase(apolloError, formatError),
		getValidationErrors: (originalError) => getValidationErrorsBase(originalError, formatError),
		normalizeBackendErrors,
		toValidationErrors: (axiosError) => toValidationErrorsBase(axiosError, formatError),
	};
}
